import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, addDoc, getDocs, query, orderBy, serverTimestamp, Timestamp, deleteDoc, doc, updateDoc, increment } from 'firebase/firestore';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // エディタのスタイル
import './Blog.css';
import PageTitle from "../../components/PageTitle";
import AdBanner from "../../components/AdBanner";

import { GiRoundStar } from "react-icons/gi";
import { RiRssLine } from "react-icons/ri";

// --- Quillのカスタマイズ: 水平線（hr）を許可する設定 ---
const Quill = ReactQuill.Quill;
const BlockEmbed = Quill.import('blots/block/embed');
class Hr extends BlockEmbed {
  static create() {
    return super.create();
  }
}
Hr.blotName = 'hr';
Hr.tagName = 'hr';
Quill.register(Hr);
// --------------------------------------------------

interface BlogPost {
  id: string;
  title: string;
  content: string;
  createdAt: Timestamp; // 投稿日時
  updatedAt?: Timestamp; // 最終更新日時
  tags?: string[]; // ★ 追加: タグの配列
  isDraft?: boolean; // ★ 追加: 下書きフラグ
  likes?: number; // ★ 追加: いいね数
}

// ★ 追加: 画像をリサイズ・圧縮するユーティリティ (コンポーネントの外に移動して参照を安定させる)
const compressImage = (file: File): Promise<Blob | File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        const maxWidth = 1200;
        if (width > maxWidth) {
          height = (maxWidth / width) * height;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else resolve(file);
        }, 'image/jpeg', 0.7);
      };
    };
    reader.onerror = reject;
  });
};

const Blog = ({ isAdmin }: { isAdmin: boolean }) => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false); // モーダル開閉ステート
  const [currentPostId, setCurrentPostId] = useState<string | null>(null); // 編集中の記事ID
  const [originalIsDraft, setOriginalIsDraft] = useState<boolean | null>(null); // 編集開始時の下書き状態
  const [currentTags, setCurrentTags] = useState<string[]>([]); // ★ 追加: 現在編集中のタグ
  const [tagInput, setTagInput] = useState(''); // ★ 追加: タグ入力フィールドの値
  const [isDraft, setIsDraft] = useState(false); // ★ 追加: 下書き状態
  const [likedPosts, setLikedPosts] = useState<string[]>([]); // いいね済みIDのリスト
  const [isPanelOpen, setIsPanelOpen] = useState(false); // ★ 追加: コントロールパネルの開閉状態
  const [tempSearchInput, setTempSearchInput] = useState(''); // 検索入力フィールドの一時的な値
  const [searchQuery, setSearchQuery] = useState(''); // ★ 追加: 検索クエリ
  const [currentPage, setCurrentPage] = useState(1); // 現在のページ (1から始まる)
  const [activeTab, setActiveTab] = useState<'public' | 'draft'>('public'); // 管理者用: 公開/下書きタブ切り替え
  const postsPerPage = 10; // ★ 追加: 1ページあたりの記事数
  const navigate = useNavigate();
  const quillRef = useRef<ReactQuill>(null);

  const [searchParams] = useSearchParams();

  // バックアップの復元を確認する処理
  const checkAndRestoreBackup = useCallback((editingPostId: string | null = null) => {
    const savedBackup = localStorage.getItem('blog_draft_backup');
    if (savedBackup) {
      try {
        const backup = JSON.parse(savedBackup);
        // 編集対象のIDとバックアップのIDが一致する場合、あるいは新規作成で双方がnullの場合
        if (backup.currentPostId === editingPostId) {
          if (window.confirm('前回の未保存の下書きデータがあります。復元しますか？')) {
            setTitle(backup.title || '');
            setContent(backup.content || '');
            setCurrentTags(backup.currentTags || []);
            setIsDraft(backup.isDraft || false);
            return true;
          } else {
            localStorage.removeItem('blog_draft_backup');
          }
        }
      } catch (e) {
        console.error('Failed to parse backup', e);
      }
    }
    return false;
  }, []);

  // 編集モードでモーダルを開く
  const handleEditOpen = useCallback((post: BlogPost) => {
    setCurrentPostId(post.id);
    setOriginalIsDraft(post.isDraft || false);
    setIsModalOpen(true);
    const restored = checkAndRestoreBackup(post.id);
    if (!restored) {
      setTitle(post.title);
      setContent(post.content);
      setCurrentTags(post.tags || []); // ★ 追加: 既存のタグをセット
      setIsDraft(post.isDraft || false); // 下書き状態をセット
    }
  }, [checkAndRestoreBackup]);

  // URLパラメータからタグ検索を反映させる
  useEffect(() => {
    const tag = searchParams.get('tag');
    if (tag) {
      setSearchQuery(tag);
      setCurrentPage(1);
      setIsPanelOpen(true);
      setTempSearchInput(tag); // URLから来たタグで検索入力フィールドも初期化
    }
  }, [searchParams]);

  // URLパラメータから編集対象の記事を検出
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && posts.length > 0) {
      const targetPost = posts.find(p => p.id === editId);
      if (targetPost) {
        handleEditOpen(targetPost);
      }
    }
  }, [searchParams, posts, handleEditOpen]);

  // searchQueryが変更されたら、検索入力フィールドの値を同期する
  useEffect(() => {
    setTempSearchInput(searchQuery);
    // 検索クエリが空になった場合、パネルを閉じる（任意）
    if (!searchQuery) setIsPanelOpen(false);
  }, [searchParams, searchQuery]);

  // モーダルオープン時にスクロールバーを無効にする
  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add('modal-open');
      document.documentElement.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
      document.documentElement.classList.remove('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
      document.documentElement.classList.remove('modal-open');
    };
  }, [isModalOpen]);

  // 自動バックアップ機能
  useEffect(() => {
    if (isModalOpen && (title || content || currentTags.length > 0 || isDraft)) {
      const backupData = {
        title,
        content,
        currentTags,
        isDraft,
        currentPostId
      };
      localStorage.setItem('blog_draft_backup', JSON.stringify(backupData));
    }
  }, [title, content, currentTags, isDraft, currentPostId, isModalOpen]);



  // ブログロゴクリック時のリセットハンドラ
  const handleResetBlogPage = () => {
    navigate('/blog'); // URLの検索パラメータをクリア
    setSearchQuery(''); // 検索クエリをリセット
    setCurrentPage(1); // ページを1ページ目に戻す
    setIsPanelOpen(false); // コントロールパネルを閉じる
    window.scrollTo({ top: 0, behavior: 'smooth' }); // ページトップへスクロール
  };
  // 画像アップロードハンドラ
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      try {
        // 1. アップロードの代わりにブラウザ側でリサイズと圧縮を実行
        const compressedBlob = await compressImage(file);

        // 2. BlobをBase64文字列に変換
        const reader = new FileReader();
        reader.readAsDataURL(compressedBlob);
        reader.onloadend = () => {
          const base64data = reader.result as string;

          // 3. 変換したBase64文字列をそのままエディタに挿入（Firestoreにテキストとして保存されれる） 
          const quill = quillRef.current?.getEditor();
          const range = quill?.getSelection();
          quill?.insertEmbed(range?.index || 0, 'image', base64data);
        };
      } catch (error) {
        console.error("Image processing failed:", error);
        alert("画像の処理に失敗しました。/n画像が大きすぎるのかも知れません。");
      }
    };
  }, []);

  // エディタのモジュール設定
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'align': ['', 'center', 'right'] }], // ''（デフォルト）を指定することで左揃えアイコンが復活します
        ['link', 'image'],
        ['hr'], // 水平線ボタンを追加
        ['clean']
      ],
      handlers: {
        // 水平線ボタンが押された時の挙動
        hr: function (this: any) {
          const range = this.quill.getSelection();
          if (range) {
            this.quill.insertEmbed(range.index, 'hr', 'null');
          }
        },
        image: imageHandler // デフォルトの挙動を自作ハンドラに差し替え
      }
    }
  }), [imageHandler]);

  // 記事一覧を取得
  const fetchPosts = async () => {
    const q = query(collection(db, 'blog'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const postsData = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        title: data.title || '', // titleも念のため
        content: data.content || '', // undefinedの場合に空文字列をデフォルトにする
        createdAt: data.createdAt || Timestamp.now(), // createdAtも念のため
        updatedAt: data.updatedAt, // updatedAtはoptionalなのでそのまま
        tags: (data.tags as string[] | undefined) || [], // タグ取得
        isDraft: data.isDraft || false, // 下書きフラグ
        likes: data.likes || 0, // いいね数
      } as BlogPost;
    });
    setPosts(postsData);
  };

  useEffect(() => {
    const savedLikes = JSON.parse(localStorage.getItem('blog_liked_posts') || '[]');
    setLikedPosts(savedLikes);
    fetchPosts();
  }, []);

  // 新規作成ボタンクリック時
  const handleCreateOpen = () => {
    setIsModalOpen(true);
    const restored = checkAndRestoreBackup(null);
    if (!restored) {
      setCurrentPostId(null);
      setOriginalIsDraft(null);
      setTitle('');
      setContent('');
      setCurrentTags([]);
      setIsDraft(false);
    }
  };

  // モーダルを閉じて入力をリセット
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentPostId(null);
    setOriginalIsDraft(null);
    setTitle('');
    setContent('');
    setCurrentTags([]); // タグもリセット
    setTagInput(''); // タグ入力もリセット
    setIsDraft(false); // 下書きリセット
    localStorage.removeItem('blog_draft_backup');
    // クエリパラメータのeditも削除する
    if (searchParams.get('edit')) {
      navigate('/blog', { replace: true });
    }
  };

  // キャンセル時に確認を挟む
  const handleCloseModalWithConfirm = () => {
    if (title || content || currentTags.length > 0) {
      if (window.confirm('編集内容を破棄してモーダルを閉じますか？（自動保存されたデータも削除されます）')) {
        handleCloseModal();
      }
    } else {
      handleCloseModal();
    }
  };

  // 編集モードでモーダルを開く


  // 記事を保存（IDを 'blog' に指定）
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !content) return;

    setIsSubmitting(true);
    try {
      if (currentPostId) {
        // 更新処理
        const postRef = doc(db, 'blog', currentPostId);

        // 下書き記事を公開（下書きから公開へ変更）した場合、公開されたタイミングをcreatedAtとし、updatedAtも同じにする
        const isDraftToPublic = originalIsDraft === true && isDraft === false;

        const updateData: any = {
          title,
          content,
          tags: currentTags,
          isDraft: isDraft
        };

        if (isDraftToPublic) {
          updateData.createdAt = serverTimestamp();
          updateData.updatedAt = serverTimestamp();
        } else {
          updateData.updatedAt = serverTimestamp();
        }

        await updateDoc(postRef, updateData);
        alert('記事を更新しました！');
      } else {
        // 新規作成処理
        await addDoc(collection(db, 'blog'), {
          title,
          content,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          tags: currentTags,
          isDraft: isDraft
        });
        alert('記事を投稿しました！');
      }

      handleCloseModal();
      fetchPosts(); // リストを更新
    } catch (error) {
      console.error("Error saving document: ", error);
      alert('保存に失敗しました。');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 記事を削除
  const handleDelete = async (postId: string) => {
    if (!window.confirm('この記事を完全に削除してもよろしいですか？')) return;

    try {
      await deleteDoc(doc(db, 'blog', postId));
      fetchPosts();
      alert('削除しました。');

    } catch (error) {
      console.error("Error deleting document: ", error);
      alert('削除に失敗しました。');
    }
  };

  // ★ 追加: タグ入力時の処理
  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && tagInput.trim() !== '') {
      e.preventDefault(); // Enterキーでのフォーム送信を防ぐ
      const newTag = tagInput.trim();
      if (!currentTags.includes(newTag)) {
        setCurrentTags([...currentTags, newTag]);
      }
      setTagInput('');
    }
  };

  // ★ 追加: タグ削除時の処理
  const handleRemoveTag = (tagToRemove: string) => {
    setCurrentTags(currentTags.filter(tag => tag !== tagToRemove));
  };

  // いいね処理
  const handleLike = async (postId: string) => {
    const isLiked = likedPosts.includes(postId);
    const postRef = doc(db, 'blog', postId);

    try {
      await updateDoc(postRef, {
        likes: increment(isLiked ? -1 : 1)
      });

      const newLikedPosts = isLiked
        ? likedPosts.filter(id => id !== postId)
        : [...likedPosts, postId];

      setLikedPosts(newLikedPosts);
      localStorage.setItem('blog_liked_posts', JSON.stringify(newLikedPosts));

      // ローカルの表示を更新
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes: Math.max(0, (p.likes || 0) + (isLiked ? -1 : 1)) } : p));
    } catch (error) {
      console.error("Error liking post:", error);
    }
  };

  // 記事のフィルタリングロジックをuseMemoで最適化
  const filteredPosts = useMemo(() => {
    let currentPosts = posts.filter(post => isAdmin || !post.isDraft);

    // 検索クエリによる絞り込み
    if (searchQuery) {
      const lowerCaseQuery = searchQuery.toLowerCase();
      currentPosts = currentPosts.filter(post =>
        post.title.toLowerCase().includes(lowerCaseQuery) ||
        post.content.toLowerCase().includes(lowerCaseQuery) ||
        post.tags?.some(tag => tag.toLowerCase().includes(lowerCaseQuery)) // ★ 追加: タグも検索対象に含める
      );
    }
    return currentPosts;
  }, [posts, isAdmin, searchQuery]);

  // ページネーションロジック
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const indexOfLastPost = currentPage * postsPerPage; // 現在のページの最後の記事のインデックス
  const indexOfFirstPost = indexOfLastPost - postsPerPage; // 現在のページの最初の記事のインデックス
  const displayedPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);

  // ページ変更ハンドラ
  const handlePageChange = (pageNumber: number) => {
    if (pageNumber < 1) {
      pageNumber = 1;
    }
    if (pageNumber > totalPages) {
      pageNumber = totalPages;
    }
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' }); // ページ変更時にスクロールをトップに戻す
  };

  // ページ番号入力ハンドラ
  const handlePageInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setCurrentPage(1); // 入力値が空の場合は1ページ目（最新の記事）に戻す
      return;
    }
    const pageNumber = parseInt(value, 10);
    if (!isNaN(pageNumber)) {
      // 表示が0ベースなので、内部ステートには+1して保存
      handlePageChange(pageNumber + 1);
    }
  };

  const [LikeHovered, setLikeHovered] = useState(false);

  return (
    <div className="blog-container" style={{ paddingTop: '100px', color: 'white', maxWidth: '800px', margin: '0 auto', paddingInline: '20px' }}>

      <div className="blog-page-wrapper"></div>
      <PageTitle title={"Blog"} />



      <div className="blog-top">
        <h1
          className="top-title"
          onClick={handleResetBlogPage}
          style={{ cursor: 'pointer', pointerEvents: 'none' }} // pointer-events: none; を上書き
        >
          Blog
        </h1>
        <a
          href="https://authentication---homepage2026.web.app/rss.xml"
          target="_blank"
          rel="noopener noreferrer"
          className="blog-rss-link"
          title="RSS フィード"
        >
          <RiRssLine />
        </a>
      </div>



      {/* 管理者用メニューエリア */}
      {isAdmin && (
        <section className="admin-menu-bar">
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <h2 style={{ color: '#ff9900', fontSize: '1rem', fontFamily: "Zen Maru Gothic", margin: 0 }}>管理者メニュー</h2>
            <div className="admin-tab-buttons" style={{ display: 'flex', gap: '10px' }}>
              <button
                className={`admin-tab-btn ${activeTab === 'public' ? 'active' : ''}`}
                onClick={() => { setActiveTab('public'); setCurrentPage(1); }}
                style={{
                  background: activeTab === 'public' ? '#ff9900' : 'rgba(255, 153, 0, 0.1)',
                  color: activeTab === 'public' ? '#000' : '#ff9900',
                  border: '1px solid #ff9900',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '0.85rem'
                }}
              >
                公開記事
              </button>
              <button
                className={`admin-tab-btn ${activeTab === 'draft' ? 'active' : ''}`}
                onClick={() => { setActiveTab('draft'); setCurrentPage(1); }}
                style={{
                  background: activeTab === 'draft' ? '#ff9900' : 'rgba(255, 153, 0, 0.1)',
                  color: activeTab === 'draft' ? '#000' : '#ff9900',
                  border: '1px solid #ff9900',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '0.85rem'
                }}
              >
                下書き一覧
              </button>
            </div>
          </div>
          <button
            className="create-btn"
            onClick={handleCreateOpen}
          >
            ＋ 記事を新規作成
          </button>
        </section>
      )}

      {/* 記事作成モーダル */}
      {isModalOpen && (
        <div className="blog-modal-overlay" onClick={handleCloseModalWithConfirm}>
          <div className="blog-modal-content" onClick={(e) => e.stopPropagation()}>

            <h2 style={{ color: '#ffffff', marginTop: 0, marginBottom: '15px', fontFamily: "Zen Maru Gothic", }}>{currentPostId ? '記事を編集' : '新規記事作成'}</h2>

            <div className="modal-two-columns">
              {/* 左カラム: タイトルと本文エディタ */}
              <div className="modal-left-column">
                <input
                  type="text"
                  placeholder="記事のタイトル"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="modal-title-input"
                />

                <div className="editor-wrapper">
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={content}
                    onChange={setContent}
                    modules={modules}
                  />
                </div>
              </div>

              {/* 右カラム: タグ、下書き設定、アクションボタン */}
              <div className="modal-right-column">
                <div>
                  <h3 style={{ color: '#ffffff', fontSize: '1rem', marginBottom: '10px', fontFamily: "Zen Maru Gothic", textAlign: 'left' }}>タグ設定</h3>
                  {/* タグ入力エリア */}
                  <div className="tag-input-area">
                    <input
                      type="text"
                      placeholder="タグを追加 (Enterで確定)"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleTagInputKeyDown}
                      className="tag-input-field"
                    />
                    <div className="tag-chips-container">
                      {currentTags.map((tag, index) => (
                        <span key={index} className="tag-chip" onClick={() => handleRemoveTag(tag)} title="クリックで削除">
                          {tag} ✕
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '20px' }}>
                  <h3 style={{ color: '#ffffff', fontSize: '1rem', marginBottom: '10px', fontFamily: "Zen Maru Gothic", textAlign: 'left' }}>公開設定</h3>
                  {/* 下書き設定エリア */}
                  <div className="draft-option-area">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#fff' }}>
                      <input
                        type="checkbox"
                        checked={isDraft}
                        onChange={(e) => setIsDraft(e.target.checked)}
                      />
                      <span style={{ fontSize: '0.9rem' }}>この記事を下書きとして保存する</span>
                    </label>
                  </div>
                </div>

                <div className="modal-actions" style={{ marginTop: '40px' }}>
                  <button type="button" className="cancel-btn" onClick={handleCloseModalWithConfirm}>キャンセル</button>
                  <button
                    type="button"
                    className="submit-btn"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? '保存中...' : (currentPostId ? '更新する' : '記事を公開する')}
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* 下部固定コントロールパネル */}
      <div className={`fixed-control-panel ${isPanelOpen ? 'panel-active' : ''}`}>
        <button
          className="panel-toggle-btn"
          onClick={() => setIsPanelOpen(!isPanelOpen)}
        >
          {isPanelOpen ? '▼ ' : '▲ '}
        </button>

        <div className="panel-content">

          {/* ページネーションコントロール*/}
          {filteredPosts.length > 0 && (

            <div className="pagination-blog-controls">
              <button
                className="pagination-blog-btn prev"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ◀
              </button>

              <input
                type="number"
                min="0"
                max={totalPages - 1}
                value={currentPage - 1} // 表示上は0から開始
                onChange={handlePageInput}
                className="pagination-blog-input"
              />

              <button
                className="pagination-blog-btn next"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                ▶
              </button>
            </div>
          )}

          {/* 検索入力 */}
          <div className="panel-search-box">
            <input
              type="text"
              placeholder="キーワード（またはタグ名）で検索..."
              value={tempSearchInput} // 一時的な入力値を使用
              onChange={(e) => setTempSearchInput(e.target.value)} // 入力ごとに一時的な値を更新
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  // Enterキーが押されたら、実際の検索クエリを更新
                  setSearchQuery(tempSearchInput);
                  setCurrentPage(1); // 検索時にページネーションをリセット
                  setIsPanelOpen(true); // 検索実行時はパネルを開いておく
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* 記事一覧表示 */}
      <div className="post-list">
        {displayedPosts.length === 0 ? (
          <p style={{ textAlign: 'center', marginTop: '50px', color: 'rgba(255,255,255,0.7)' }}>
            記事が見つかりませんでした。
          </p>
        ) : (
          displayedPosts.flatMap((post, index) => {
            const isLastPost = index === displayedPosts.length - 1;
            const isEvery5th = index > 0 && (index + 1) % 5 === 0;
            const showAdAfter = isEvery5th || (isLastPost && displayedPosts.length < 5);

            const articleElement = (
              <article
                key={post.id}
                className="post-item"
                style={{ marginBottom: '40px', borderBottom: '1px solid rgba(255,255,255,0.2)', paddingBottom: '20px' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <h3
                    style={{ fontWeight: 'bold', fontFamily: "Zen Maru Gothic", cursor: 'pointer', borderBottom: '2px solid #ffffff', textAlign: 'left', width: 'fit-content' }}
                    onClick={() => navigate(`/blog/${post.id}`)}
                  >
                    {post.isDraft && <span className="draft-badge">下書き</span>}
                    <GiRoundStar /> {post.title}
                  </h3>


                  {isAdmin && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className="edit-btn"
                        onClick={() => handleEditOpen(post)}
                        title="記事を編集"
                      >
                        編集
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(post.id)}
                        title="記事を削除"
                      >
                        削除
                      </button>
                    </div>
                  )}
                </div>

                {/* 記事のタグ表示 */}
                {post.tags && post.tags.length > 0 && (
                  <div className="post-tags">
                    {post.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="post-tag-chip"
                        onClick={() => {
                          navigate(`/blog?tag=${encodeURIComponent(tag)}`); // URLにタグを追加して遷移
                          window.scrollTo({ top: 0, behavior: 'smooth' }); // ページトップへスクロール
                        }}
                      >{tag}</span>
                    ))}
                  </div>
                )}

                {/* HTMLをそのままレンダリング */}
                {(() => {
                  const parts = post.content.split(/<hr\s*\/?>/);
                  const firstPart = parts[0] || '';
                  const hasMore = parts.length > 1 && firstPart.trim() !== post.content.trim(); // 区切り線が本当に内容を分割しているか確認
                  const displayContent = hasMore ? firstPart : post.content;
                  return (
                    <>
                      <div
                        className="post-content"
                        style={{ marginTop: '15px' }}
                        dangerouslySetInnerHTML={{ __html: displayContent }}
                      />
                      {hasMore && (
                        <Link to={`/blog/${post.id}`} className="read-more-link">続きを読む ≫</Link>
                      )}
                    </>
                  );
                })()}

                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '15px', marginTop: '20px' }}>
                  <button
                    className="like-button"
                    onClick={() => handleLike(post.id)}
                    onMouseEnter={() => setLikeHovered(true)}
                    onMouseLeave={() => setLikeHovered(false)}

                    style={{
                      background: likedPosts.includes(post.id) ? '#ff4d4f' : '#ff4d4f22',
                      border: '1px solid #ff4d4f',
                      color: likedPosts.includes(post.id) ? '#fff' : '#ff4d4f',
                      padding: window.innerWidth <= 1024 ? '8px 16px' : '4px 12px',
                      borderRadius: '20px',
                      cursor: 'pointer',
                      fontFamily: "Zen Maru Gothic",
                      fontSize: window.innerWidth <= 1024 ? '1rem' : '0.9rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',

                      opacity: LikeHovered ? 0.6 : 1,
                      transition: 'opacity 0.2s ease'
                    }}
                  >
                    ❤️ {post.likes || 0}
                  </button>

                  <small style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', margin: 0, display: 'inline-flex', alignItems: 'center' }}>
                    <Link to={`/blog/${post.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                      {post.updatedAt && !post.updatedAt.isEqual(post.createdAt)
                        ? `最終更新 : ${post.updatedAt.toDate().toLocaleString('ja-JP', {
                          year: 'numeric', month: '2-digit', day: '2-digit',
                          hour: '2-digit', minute: '2-digit'
                        })}`
                        : `投稿日時 : ${post.createdAt ? post.createdAt.toDate().toLocaleString('ja-JP', {
                          year: 'numeric', month: '2-digit', day: '2-digit',
                          hour: '2-digit', minute: '2-digit'
                        }) : ''}`}
                    </Link>
                  </small>
                </div>
              </article>
            );

            return showAdAfter
              ? [articleElement, <h3 style={{
                fontWeight: 'bold',
                fontFamily: "Zen Maru Gothic",
                fontSize: '1.9rem',
                borderBottom: '2px solid #ffffff',
                textAlign: 'left',
                width: 'fit-content'
              }}>
                <GiRoundStar /> 広告</h3>,

                <AdBanner key={`ad-${index}`} slot="6535744487" />,

                <small style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', display: 'flex', justifyContent: 'flex-end' }}>
                  AdSpace
                </small>,

                <div style={{ paddingTop: '1rem', borderBottom: '1px solid rgba(255,255,255,0.2)' }} />
              ]
              : [articleElement];
          })
        )}
      </div>

    </div>
  );
};

export default Blog;